/**
 * src/components/agent/TurnList.jsx — ordered turn renderer with
 * day-boundary separators.
 *
 * 2026-04-21 redesign (F7 usability pass). Previous version rendered
 * bubbles alone; the CEO couldn't scan past conversations for context
 * because there were no visual anchors between days. Now inserts a
 * date divider each time `created_at` crosses a day boundary — that's
 * the visual hook for "find Monday's Maranhão discussion" when
 * scrolling back.
 *
 * Sorting invariant unchanged: `created_at ASC`, stable; ties broken
 * by `id`.
 *
 * Props:
 *   - turns: array of chat_messages rows (role/content/source/created_at/id/speaker_label)
 *   - assistantName: resolved name for role=assistant (default inside TurnBubble)
 *   - streamingTurnId: id of the assistant turn currently receiving SSE tokens (optional)
 *   - style: merged into the scroller
 */

import { useMemo } from 'react';
import TurnBubble from './TurnBubble.jsx';
import { useStickyScroll } from '../../lib/hooks/useStickyScroll';

function sameDay(a, b) {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear()
    && da.getMonth() === db.getMonth()
    && da.getDate() === db.getDate()
  );
}

function formatDayLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
}

function DateDivider({ iso }) {
  return (
    <div
      data-testid="turn-date-divider"
      data-iso={iso}
      role="separator"
      aria-label={formatDayLabel(iso)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '14px 0 6px',
        userSelect: 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{ flex: 1, height: 1, background: 'var(--bdr)' }}
      />
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--tm)',
          letterSpacing: 0.6,
          textTransform: 'uppercase',
        }}
      >
        {formatDayLabel(iso)}
      </span>
      <span
        aria-hidden="true"
        style={{ flex: 1, height: 1, background: 'var(--bdr)' }}
      />
    </div>
  );
}

export default function TurnList({
  turns = [],
  assistantName,
  streamingTurnId = null,
  // TICKET-073 (karaoke) — the turn currently being spoken + the reply-global
  // word index the TTS is on. Forwarded only to the matching bubble.
  karaokeTurnId = null,
  karaokeWordIndex = -1,
  style,
  ...rest
}) {
  const ordered = useMemo(() => {
    const list = Array.isArray(turns) ? turns.slice() : [];
    list.sort((a, b) => {
      const ta = new Date(a?.created_at || 0).getTime();
      const tb = new Date(b?.created_at || 0).getTime();
      if (ta !== tb) return ta - tb;
      return String(a?.id || '').localeCompare(String(b?.id || ''));
    });
    return list;
  }, [turns]);

  // Build render items: insert a DateDivider wherever the day changes
  // (including before the first turn if it has a date). The key for a
  // divider uses the iso itself so two dividers on the same day never
  // exist.
  const items = useMemo(() => {
    const out = [];
    let prevDay = null;
    for (const turn of ordered) {
      const iso = turn?.created_at;
      if (iso) {
        if (!prevDay || !sameDay(prevDay, iso)) {
          out.push({ kind: 'divider', iso, key: `divider:${new Date(iso).toDateString()}` });
          prevDay = iso;
        }
      }
      out.push({ kind: 'turn', turn, key: turn.id || iso || Math.random() });
    }
    return out;
  }, [ordered]);

  // Task 1.4 — sticky-bottom auto-scroll. Watches the ordered turn
  // count so each new turn scrolls the latest bubble into view, unless
  // the user has manually scrolled up (then auto-scroll suspends
  // until they return to the bottom).
  const { containerRef, onScroll } = useStickyScroll([ordered.length]);

  return (
    <div
      ref={containerRef}
      onScroll={onScroll}
      data-testid="turn-list"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        width: '100%',
        ...style,
      }}
      {...rest}
    >
      {items.map((item) => (
        item.kind === 'divider'
          ? <DateDivider key={item.key} iso={item.iso} />
          : (
            <TurnBubble
              key={item.key}
              turn={item.turn}
              assistantName={assistantName}
              isStreaming={streamingTurnId && item.turn.id === streamingTurnId}
              karaokeActive={!!karaokeTurnId && item.turn.id === karaokeTurnId}
              karaokeWordIndex={(!!karaokeTurnId && item.turn.id === karaokeTurnId) ? karaokeWordIndex : -1}
            />
          )
      ))}
    </div>
  );
}
